<template>
  <div class="p-series-compare" aria-labelledby="p-series-comparison-title">
    <header class="p5-comparison__heading">
      <h2 id="p-series-comparison-title">{{ t('comparison.title') }}</h2>
    </header>
    <div class="p5-comparison__panel">
      <article class="p5-product-card">
        <img :src="fixedComparisonProduct.image" :alt="formatMessage('accessibility.comparisonProductAlt', { model: fixedComparisonProduct.name })" loading="lazy">
        <div class="p5-product-card__model"><h3>{{ fixedComparisonProduct.name }}</h3></div>
        <ul class="p5-product-card__functions">
          <li v-for="feature in comparisonFeatureRows" :key="feature.key" :class="{ 'is-unsupported': !fixedComparisonProduct.features.includes(feature.key) }">
            {{ fixedComparisonProduct.features.includes(feature.key) ? t(feature.labelKey) : '-' }}
          </li>
        </ul>
        <dl>
          <div v-for="specification in comparisonSpecifications" :key="specification.key">
            <dt>{{ t(specification.labelKey) }}</dt><dd>{{ t(fixedComparisonProduct.specificationKeys[specification.key]) }}</dd>
          </div>
        </dl>
      </article>
      <article class="p5-product-card p5-product-card--selectable">
        <img :src="selectedComparisonProduct.image" :alt="formatMessage('accessibility.comparisonProductAlt', { model: selectedComparisonProduct.name })" loading="lazy">
        <div ref="comparisonSelector" class="p5-product-card__model p5-comparison-selector">
          <h3>{{ selectedComparisonProduct.name }}</h3>
          <button ref="comparisonMenuButton" class="p5-comparison-selector__trigger" type="button" aria-haspopup="listbox" aria-controls="comparison-product-options" :aria-expanded="isComparisonMenuOpen ? 'true' : 'false'" :aria-label="t('accessibility.comparisonChoose')" @click="toggleComparisonMenu" @keydown="handleComparisonTriggerKeydown">
            <span aria-hidden="true"></span>
          </button>
          <div v-if="isComparisonMenuOpen" id="comparison-product-options" class="p5-comparison-selector__menu" role="listbox" :aria-label="t('accessibility.comparisonList')">
            <button v-for="(product, index) in comparisonOptions" :id="`comparison-option-${product.key}`" :key="product.key" ref="comparisonOptionButtons" class="p5-comparison-selector__option" :class="{ 'is-selected': product.key === selectedComparisonKey }" type="button" role="option" :aria-selected="product.key === selectedComparisonKey ? 'true' : 'false'" @click="selectComparisonProduct(product.key)" @keydown="handleComparisonOptionKeydown($event, index)">
              {{ product.name }}
            </button>
          </div>
        </div>
        <ul class="p5-product-card__functions">
          <li v-for="feature in comparisonFeatureRows" :key="feature.key" :class="{ 'is-unsupported': !selectedComparisonProduct.features.includes(feature.key) }">
            {{ selectedComparisonProduct.features.includes(feature.key) ? t(feature.labelKey) : '-' }}
          </li>
        </ul>
        <dl>
          <div v-for="specification in comparisonSpecifications" :key="specification.key">
            <dt>{{ t(specification.labelKey) }}</dt><dd>{{ t(selectedComparisonProduct.specificationKeys[specification.key]) }}</dd>
          </div>
        </dl>
      </article>
    </div>
  </div>
</template>

<script>
import P5 from '@/views/products/P5.vue'
import { P5_MESSAGES } from '@/views/products/p5-i18n'

// Borrow the existing P5 interactions and catalog; do not maintain another table.
const comparisonMethodNames = [
  'getP5Message', 't', 'formatMessage', 'toggleComparisonMenu', 'openComparisonMenu',
  'closeComparisonMenu', 'focusComparisonOption', 'moveComparisonOptionFocus',
  'selectComparisonProduct', 'handleComparisonTriggerKeydown',
  'handleComparisonOptionKeydown', 'handleComparisonDocumentPointer'
]
const comparisonMethods = Object.fromEntries(comparisonMethodNames.map(name => [name, P5.methods[name]]))

export default {
  name: 'PSeriesCompare',
  props: {
    fixedKey: { type: String, required: true, validator: key => ['p3', 'p1'].includes(key) },
    initialComparisonKey: { type: String, required: true }
  },
  data() {
    const source = P5.data()
    const products = [source.fixedComparisonProduct, ...source.comparisonOptions]
    const comparisonOptions = products.filter(product => product.key !== this.fixedKey)
    return {
      fixedComparisonProduct: products.find(product => product.key === this.fixedKey),
      comparisonOptions,
      comparisonFeatureRows: source.comparisonFeatureRows,
      comparisonSpecifications: source.comparisonSpecifications,
      selectedComparisonKey: comparisonOptions.some(product => product.key === this.initialComparisonKey)
        ? this.initialComparisonKey : comparisonOptions[0].key,
      isComparisonMenuOpen: false,
      comparisonFocusIndex: 0,
      missingP5TranslationWarnings: source.missingP5TranslationWarnings,
      currentLanguage: 'ZH'
    }
  },
  computed: {
    currentMessages() { return P5_MESSAGES['zh-CN'] },
    selectedComparisonProduct: P5.computed.selectedComparisonProduct
  },
  mounted() { document.addEventListener('mousedown', this.handleComparisonDocumentPointer) },
  beforeUnmount() { document.removeEventListener('mousedown', this.handleComparisonDocumentPointer) },
  methods: comparisonMethods
}
</script>

<style scoped src="../assets/styles/p-series-compare.css"></style>
